import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'

const API = axios.create({
  baseURL: (import.meta.env.VITE_API_URL ?? '') + '/api',
  withCredentials: true,
  timeout: 10000,
})

let refreshPromise: Promise<unknown> | null = null

const AUTH_PATHS = [
  '/auth/login',
  '/auth/login-email',
  '/auth/register',
  '/auth/register-email',
  '/auth/verify-otp',
  '/auth/forgot-password',
]

// Attach stored access token as Bearer header on every request
API.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

API.interceptors.response.use(
  (res) => res,

  async (err) => {
    const originalRequest = err.config
    const status: number | undefined = err.response?.status

    if (AUTH_PATHS.some(p => originalRequest.url?.includes(p))) {
      return Promise.reject(err)
    }

    if (status !== 401) {
      const msg: string =
        err.response?.data?.message ??
        err.response?.data?.error ??
        'Une erreur est survenue'
      toast.error(msg, { id: 'api-error', duration: 4000 })
      return Promise.reject(err)
    }

    if (originalRequest.url?.includes('/auth/refresh')) {
      refreshPromise = null
      useAuthStore.getState().clearUser()
      window.location.replace('/login')
      return Promise.reject(err)
    }

    if (originalRequest._retry) {
      useAuthStore.getState().clearUser()
      window.location.replace('/login')
      return Promise.reject(err)
    }

    originalRequest._retry = true

    if (!refreshPromise) {
      refreshPromise = API.post('/auth/refresh')
        .then((res) => {
          const newToken: string | undefined = res.data?.accessToken
          if (newToken) useAuthStore.getState().setAccessToken(newToken)
        })
        .finally(() => { refreshPromise = null })
    }

    try {
      await refreshPromise
      return API.request(originalRequest)
    } catch {
      useAuthStore.getState().clearUser()
      window.location.replace('/login')
      return Promise.reject(err)
    }
  }
)

export default API
